import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled as styling } from "@mui/material/styles";
import withTheme from "@mui/styles/withTheme";
import ReactPlayer from "react-player";
import { animated } from "@react-spring/web";
import styled from "styled-components";
import Color from "../../../assets/colors";

// Typography Components //
export const FilterSubtitle = styling(Typography)((props) => ({
  color: Color.white,
  display: "inline-block",
  alignSelf: "center",
  fontSize: "24px",
  fontWeight: "400",

  [props.theme.breakpoints.down("lg")]: {
    fontSize: "20px",
  },

  [props.theme.breakpoints.down(768)]: {
    fontSize: "14px",
  },
}));

// Grid Components //
export const GridLinearOverlay = styling(Grid)({
  background:
    "linear-gradient(180deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 20%)",
  position: "absolute",
  top: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  zIndex: 1,
});

export const GridFilters = styling(Grid)<{ $isVisible: boolean }>((props) => ({
  backgroundColor: props.$isVisible ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)",
  position: "absolute",
  top: 0,
  width: "100%",
  height: "100%",
  padding: "30px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  transition: "background-color 0.6s ease",
  pointerEvents: "none",
  zIndex: 2,

  "& > *": {
    pointerEvents: "auto",
  },

  [props.theme.breakpoints.down(480)]: {
    padding: "10px",
  },
}));

export const GridFilterContainer = styling(Grid)({
  width: "79%",
  maxWidth: "960px",
  minWidth: "300px",
  flexDirection: "column",
  justifyContent: "flex-start",
  height: "100%",
  display: "flex",
  paddingTop: "20px",
});

export const GridHeaderContainer = styling(Grid)((props) => ({
  position: "absolute",
  top: "20px",
  left: "20px",
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "20px",
  zIndex: 10,
  cursor: "pointer",
  maxWidth: "calc(100% - 60px)",

  [props.theme.breakpoints.down(768)]: {
    gap: "15px",
    top: "20px",
    left: "20px",
    maxWidth: "calc(100% - 40px)",
  },

  [props.theme.breakpoints.down(480)]: {
    gap: "10px",
    top: "10px",
    left: "10px",
    maxWidth: "calc(100% - 20px)",
  },
}));

export const GridTitle = styling(Grid)({
  textAlign: "left",
  width: "170px",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  flex: 1,
});

export const GridContainer = styling(Grid)({
  display: "flex",
  flexDirection: "row",
  marginTop: "10px",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
});

export const GridContainerInner = styling(Grid)({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "baseline",
  height: "42px",
  marginTop: "15px",
});

export const TextFieldContainer = styling(Grid)((props) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "baseline",
  maxWidth: "240px",

  [props.theme.breakpoints.down("sm")]: {
    maxWidth: "280px",
    display: "flex",
    justifyContent: "center",
  },
}));

export const GridCompanyLogo = styling(Grid)((props) => ({
  position: "absolute",
  bottom: "15px",
  right: "25px",
  display: "flex",
  justifyContent: "flex-end",
  flexDirection: "column",
  alignItems: "center",

  [props.theme.breakpoints.down(480)]: {
    alignItems: "flex-end",
    bottom: "15px",
    right: "40%",
  },
}));

export const GridLogoContainer = styling(Grid)((props) => ({
  cursor: "pointer",
  width: "72px",
  height: "72px",
  flexShrink: 0,

  [props.theme.breakpoints.down(768)]: {
    width: "60px",
    height: "60px",
  },

  [props.theme.breakpoints.down(480)]: {
    width: "50px",
    height: "50px",
  },
}));

export const GridPlayButtonContainer = styling(Grid)((props) => ({
  height: "35px",
  marginTop: "0.75rem",

  [props.theme.breakpoints.down(480)]: {
    height: "0px",
  },
}));

export const GridPlayButtonOverlay = styling(Grid)<{ $isVisible: boolean }>(
  (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: props.$isVisible ? 1 : 0,
    transition: "opacity 0.3s ease",
    pointerEvents: props.$isVisible ? "auto" : "none",
    zIndex: 10,
  })
);

export const ButtonGrid = styling(Grid)((props) => ({
  [props.theme.breakpoints.down(780)]: {
    marginTop: "20px",
  },
}));

// Misc. Components //
export const ContainerStyled = styling("div")({
  position: "relative",
  margin: "60px 8.7% 40px 8.7%",
  cursor: "pointer",
  borderRadius: "10px",
  overflow: "hidden",
  backgroundColor: Color.black,
});

export const IconButtonStyled = styling(IconButton)((props) => ({
  color: Color.white,
  padding: "20px",
  backgroundColor: "transparent",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "3rem",
  },

  [props.theme.breakpoints.down(768)]: {
    padding: "15px",

    "& .MuiSvgIcon-root": {
      fontSize: "2.5rem",
    },
  },

  [props.theme.breakpoints.down(480)]: {
    padding: "12px",

    "& .MuiSvgIcon-root": {
      fontSize: "2rem",
    },
  },
}));

export const ReactPlayerStyled = styling(ReactPlayer)((props) => ({
  objectFit: "cover",

  [props.theme.breakpoints.down(780)]: {
    height: "55vh !important",
  },

  [props.theme.breakpoints.down(480)]: {
    height: "500px !important",
  },
}));

export const ImageStyled = styling("img")({
  backgroundColor: Color.white,
  borderRadius: "10px",
  objectFit: "scale-down",
  width: "100%",
  height: "100%",
});

export const TextFieldStyled = styling(TextField)({
  color: Color.white,
  marginLeft: "12px",
  width: "35%",
  minWidth: "200px",

  input: {
    textAlign: "center",
    color: Color.white,
    fontSize: "1.6rem",
  },

  label: {
    color: Color.white,
    fontSize: "1.6rem",
  },

  "& ::placeholder": {
    color: Color.white,
    opacity: 1,
  },

  "& .MuiInputBase-input:focus::placeholder": {
    color: "transparent",
  },

  "  & .MuiInputBase-input": {
    borderColor: `${Color.white} !important`,
  },

  "& .MuiInput-underline::before": {
    borderColor: `${Color.white} !important`,
  },

  "& .MuiInput-underline:hover:before": {
    borderColor: `${Color.white} !important`,
  },

  "& .MuiInput-underline::after": {
    borderColor: `${Color.white} !important`,
  },
});

export const TitleStyled = withTheme(styled(animated(Typography))`
  && {
    color: #fff;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.15px;
    margin-bottom: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    text-align: left;
    text-transform: uppercase;

    ${(props) => props.theme.breakpoints.down("lg")} {
      font-size: 14px;
      line-height: 20px;
    }

    ${(props) => props.theme.breakpoints.down(768)} {
      font-size: 12px;
      line-height: 18px;
    }

    ${(props) => props.theme.breakpoints.down(480)} {
      font-size: 11px;
      line-height: 16px;
    }
  }
`);
